/**
 * Pagination hook for managing paginated data
 *
 * Provides pagination functionality with automatic scrolling to top/left
 * when page changes. Useful for lists, tables, and data grids.
 *
 * @example
 * ```tsx
 * const { currentData, currentPage, maxPage, goToPage, nextPage, prevPage } = usePagination(
 *   blogPosts,
 *   10, // items per page
 *   0,  // scroll to top
 *   0   // scroll to left
 * )
 *
 * return (
 *   <div>
 *     {currentData.map(post => <PostCard key={post.id} post={post} />)}
 *     <button onClick={prevPage} disabled={currentPage === 1}>Previous</button>
 *     <span>Page {currentPage} of {maxPage}</span>
 *     <button onClick={nextPage} disabled={currentPage === maxPage}>Next</button>
 *   </div>
 * )
 * ```
 */
import React from 'react'

/**
 * Pagination hook return type
 */
interface UsePaginationReturn<T> {
  currentData: T[]
  currentPage: number
  maxPage: number
  goToPage: (page: number) => void
  nextPage: () => void
  prevPage: () => void
}

/**
 * Custom hook for pagination
 *
 * @param data - Array of data items to paginate
 * @param itemsPerPage - Number of items to show per page
 * @param scrollToTop - Y position to scroll to when page changes (0 = top)
 * @param scrollToLeft - X position to scroll to when page changes (0 = left)
 * @returns Object containing:
 *   - `currentData`: Array of items for the current page
 *   - `currentPage`: Current page number (1-indexed)
 *   - `maxPage`: Total number of pages
 *   - `goToPage`: Function to navigate to a specific page
 *   - `nextPage`: Function to go to the next page
 *   - `prevPage`: Function to go to the previous page
 */
function usePagination<T>(
  data: T[],
  itemsPerPage: number,
  scrollToTop: number,
  scrollToLeft: number,
): UsePaginationReturn<T> {
  const [currentPage, setCurrentPage] = React.useState(1)
  const maxPage = Math.ceil(data.length / itemsPerPage)

  const currentData = React.useMemo(() => {
    const begin = (currentPage - 1) * itemsPerPage
    const end = begin + itemsPerPage
    return data.slice(begin, end)
  }, [data, currentPage, itemsPerPage])

  const goToPage = (page: number) => {
    const pageNumber = Math.max(1, Math.min(page, maxPage))
    setCurrentPage(pageNumber)
    if (typeof window === 'undefined') return
    window.scroll({
      top: scrollToTop,
      left: scrollToLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }

  const nextPage = () => goToPage(currentPage + 1)
  const prevPage = () => goToPage(currentPage - 1)

  return { currentData, currentPage, maxPage, goToPage, nextPage, prevPage }
}

export default usePagination
